import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import Header from "../components/layout/Header";
import { styled, useTheme } from "@mui/material/styles";
import useLocalizationStore from "../store/zustand/localization";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import Sidebar from "../components/navigation/Sidebar";
import {
  Box,
  Button,
  Toolbar,
  IconButton,
  Typography,
  useMediaQuery,
} from "@mui/material";
import useUserStore from "../store/zustand/user";
import { hasActiveSubscription } from "../store/utils/planUtils";
import KeyboardArrowLeftOutlinedIcon from "@mui/icons-material/KeyboardArrowLeftOutlined";
import KeyboardArrowRightOutlinedIcon from "@mui/icons-material/KeyboardArrowRightOutlined";

const DRAWER_WIDTH = 260;
const COLLAPSED_WIDTH = 80;

const Main = styled("main")(({ theme }) => ({
  flexGrow: 1,
  padding: theme.spacing(4),
  width: `calc(100% - ${DRAWER_WIDTH}px)`,
  transition: theme.transitions.create(["margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  backgroundColor: theme.palette.background.default,
  minHeight: "100vh",
  position: "relative",
  [theme.breakpoints.down("md")]: {
    marginLeft: 0,
    padding: theme.spacing(3),
  },
  [theme.breakpoints.down("sm")]: {
    marginLeft: 0,
    padding: theme.spacing(2),
  },
}));

function DashboardLayout({ children }) {
  const theme = useTheme();
  const { t } = useTranslation();
  const { language } = useLocalizationStore();
  const location = useLocation();
  const navigate = useNavigate();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const [open, setOpen] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user, subscription_status } = useUserStore();

  const roleName = user?.role?.name || user?.role;
  const isSuperAdmin = roleName === "super_admin"; // Check for SuperAdmin role
  const isSupplier = user?.is_supplier;

  const hasSubscription = hasActiveSubscription(user, subscription_status);
  const isSubscriptionPage = location.pathname.startsWith("/subscription");
  const isEnterprisePricingPage = location.pathname.startsWith("/admin/enterprise-pricing");

  // Determine if subscription overlay should show
  //todo : when subscribe plan pending api
  const showSubscriptionOverlay =
    !isSuperAdmin && !hasSubscription && !isSubscriptionPage && !isEnterprisePricingPage;

  // Set scroll behavior based on subscription status and page
  useEffect(() => {
    if (showSubscriptionOverlay && !isSuperAdmin) {
      document.body.style.overflow = "hidden"; // Disable scrolling on all pages (non-SuperAdmin, no subscription)
    } else if (isSubscriptionPage || isEnterprisePricingPage) {
      document.body.style.overflow = "auto"; // Enable scrolling on the subscription page
    } else {
      document.body.style.overflow = "auto"; // Enable scrolling for other pages with active subscription or SuperAdmin
    }
  }, [showSubscriptionOverlay, isSubscriptionPage, isEnterprisePricingPage, isSuperAdmin]);

  const pageTitle = isSuperAdmin
    ? t("nav.super")
    : isSupplier
    ? t("nav.Supplier")
    : t(`nav.admin`);

  const handleToggle = () => {
    if (isDesktop) {
      setOpen((prev) => !prev);
    } else {
      setMobileOpen((prev) => !prev);
    }
  };

  const handleGoToSubscription = () => {
    navigate("/subscription", { replace: false });
  };

  return (
    <Box
      sx={{
        display: "flex",
        backgroundColor: theme.palette.background.default,
        width: "100%",
        overflow: "hidden", // Prevent main layout from scrolling
      }}
    >
      <Header
        drawerWidth={DRAWER_WIDTH}
        collapsedWidth={COLLAPSED_WIDTH}
        open={open}
        onToggleSidebar={handleToggle}
        title={pageTitle}
      />
      <IconButton
        onClick={handleToggle}
        edge="start"
        size="small"
        color="inherit"
        sx={{
          display: { xs: "none", md: "inline-flex" },
          position: "fixed",
          [language === "ar" ? "right" : "left"]: !open
            ? COLLAPSED_WIDTH - 14
            : DRAWER_WIDTH - 14,
          top: 40,
          zIndex: 9991,
          border: "1px solid #EEEEEE",
          backgroundColor: "white",
          "&:hover": { backgroundColor: "rgba(255,255,255)" },
        }}
      >
        {open ? (
          language === "ar" ? (
            <KeyboardArrowRightOutlinedIcon />
          ) : (
            <KeyboardArrowLeftOutlinedIcon />
          )
        ) : language === "ar" ? (
          <KeyboardArrowLeftOutlinedIcon />
        ) : (
          <KeyboardArrowRightOutlinedIcon />
        )}
      </IconButton>
      <Sidebar
        drawerWidth={DRAWER_WIDTH}
        collapsedWidth={COLLAPSED_WIDTH}
        open={open}
        onToggleSidebar={handleToggle}
        currentPath={location.pathname}
        variant="permanent"
      />
      <Sidebar
        variant="temporary"
        drawerWidth={DRAWER_WIDTH}
        collapsedWidth={COLLAPSED_WIDTH}
        open={mobileOpen}
        onToggleSidebar={handleToggle}
        currentPath={location.pathname}
      />
      <Main
        sx={{
          padding: showSubscriptionOverlay ? 0 : theme.spacing(4),
          overflowY: "auto", // Enable scrolling on the main content area
        }}
      >
        <Box
          sx={{
            position: "relative",
            minHeight: "100%",
          }}
        >
          <Box
            sx={{
              filter: showSubscriptionOverlay ? "blur(4px)" : "none",
              pointerEvents: showSubscriptionOverlay ? "none" : "auto",
              transition: "filter 150ms ease",
              minHeight: "100%",
            }}
          >
            <Toolbar />
            <Outlet />
          </Box>
          {showSubscriptionOverlay && (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                px: 3,
                py: 6,
                zIndex: 2,
                backgroundColor: "rgba(42, 42, 42, 0.5)",
                backdropFilter: "blur(6px)",
              }}
            >
              <Box sx={{ bgcolor: "white", p: 3, py: 5, borderRadius: 2 }}>
                <Typography variant="h5" fontWeight={600} gutterBottom>
                  {t("subscription.lockedTitle", "Subscription Required")}
                </Typography>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ maxWidth: 420, mb: 3 }}
                >
                  {t(
                    "subscription.lockedDescription",
                    "Activate a plan to unlock dashboard features. You can still use the sidebar to review available plans."
                  )}
                </Typography>
                <Button variant="contained" onClick={handleGoToSubscription}>
                  {t("subscription.choosePlanCta", "Choose a Plan")}
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      </Main>
    </Box>
  );
}

DashboardLayout.propTypes = {
  children: PropTypes.node.isRequired,
};

export default DashboardLayout;
